import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, FileText, ArrowRight, Download, ClipboardCheck } from "lucide-react";
import { adminApi } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCompletionQueue() {
  const [status, setStatus] = useState("submitted");
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [kpi, setKpi] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const [q, k] = await Promise.all([
        axios.get(`${API}/admin/jobcards/completion-queue?status=${status}`),
        axios.get(`${API}/admin/reports/onsite-time?days=90`),
      ]);
      setRows(q.data); setKpi(k.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openDetail = async (id) => {
    try { setDetail((await axios.get(`${API}/admin/jobcards/${id}`)).data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not open"); }
  };

  const approve = async () => {
    if (!detail) return;
    if (!window.confirm(`Approve job card #${detail.job_number}? This finalises the card, generates the PDF and unlocks tax-invoice issuance for the job.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/jobcards/${detail.id}/approve`);
      toast.success("Approved. PDF ready.");
      setDetail(null);
      await load();
      // Trigger PDF download
      window.open(`${API}/admin/jobcards/${r.data ? detail.id : ""}/pdf.pdf`, "_blank");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.validation_errors) toast.error(`${d.validation_errors.length} validation error(s)`);
      else toast.error(d || "Could not approve");
    } finally { setBusy(false); }
  };

  const doReturn = async () => {
    if (note.trim().length < 3) return toast.error("Give a return note");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/jobcards/${returnFor.id}/return`, { admin_note: note.trim() });
      toast.success("Returned to fitter");
      setReturnFor(null); setNote(""); setDetail(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not return"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-completion-queue-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Completion Queue</h1>
      <p className="text-[#3f4b52] mt-1">Job cards submitted for review, approve, or return.</p>

      {/* KPI card */}
      {kpi && (
        <div className="mt-4 bg-white border border-[#cfd7db] rounded-md p-4" data-testid="kpi-onsite">
          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">On-site time (last 90 days)</div>
          <div className="mt-1 flex flex-wrap items-baseline gap-6">
            <div>
              <div className="text-3xl font-display font-black text-[#f28a16]">
                {kpi.actual?.median ?? "—"}<span className="text-base text-[#3f4b52]"> min median</span>
              </div>
              <div className="text-xs text-[#3f4b52]">Average {kpi.actual?.average ?? "—"} min · {kpi.sample_size} samples</div>
            </div>
            {kpi.variance && (
              <div>
                <div className="text-2xl font-display font-black text-[#111619]">
                  {kpi.variance.median > 0 ? "+" : ""}{kpi.variance.median} min<span className="text-sm text-[#3f4b52]"> vs quoted</span>
                </div>
                <div className="text-xs text-[#3f4b52]">Sample size {kpi.variance.count}</div>
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-[#3f4b52] italic">{kpi.explanation}</p>
        </div>
      )}

      <div className="mt-4 inline-flex bg-white border border-[#cfd7db] rounded-md p-1" data-testid="cq-filter">
        {["submitted", "returned", "approved", "all"].map((f) => (
          <button key={f} onClick={() => setStatus(f)} data-testid={`cq-filter-${f}`}
                  className={`px-3 py-1.5 text-xs font-display uppercase tracking-widest rounded ${status === f ? "bg-[#f28a16] text-white" : "text-[#3f4b52]"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {!rows && <div className="text-sm text-[#3f4b52]"><Loader2 className="inline animate-spin" /> Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-10 text-center text-[#3f4b52]" data-testid="cq-empty">
            Nothing here.
          </div>
        )}
        {rows?.map((r) => (
          <div key={r.id} className="bg-white border border-[#cfd7db] rounded-md p-4 flex items-start justify-between gap-3 flex-wrap"
               data-testid={`cq-row-${r.id}`}>
            <div className="min-w-0">
              <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">
                Job Card · {r.status} · Job #{r.job_number}
              </div>
              <div className="font-display font-bold text-[#111619] text-lg">
                Submitted {r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-ZA") : "—"}
              </div>
              <div className="text-xs text-[#3f4b52]">By {r.submitted_by || "—"} · Overall {r.overall_rating || "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              {r.status === "approved" && (
                <button
                  onClick={() => adminApi.openDoc(`${API}/admin/jobcards/${r.id}/pdf.pdf`)}
                  type="button"
                  data-testid={`cq-pdf-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[#f28a16] hover:underline">
                  <Download size={12} /> PDF
                </button>
              )}
              <button onClick={() => openDetail(r.id)} data-testid={`cq-open-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border rounded text-xs font-display uppercase tracking-widest">
                Open <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {detail && (
        <Modal onClose={() => setDetail(null)} testId="cq-detail-modal">
          <div className="flex items-center justify-between">
            <div className="font-display uppercase tracking-widest text-sm">Job Card · #{detail.job_number}</div>
            <span className="text-xs px-2 py-0.5 rounded bg-[#111619] text-white uppercase tracking-widest">{detail.status}</span>
          </div>
          <div className="text-xs text-[#3f4b52] space-y-1">
            <div><b>Rating:</b> {detail.overall_rating || "—"}</div>
            <div><b>Remarks:</b> {detail.remarks || "—"}</div>
            <div><b>VIN:</b> {detail.vehicle_details?.vin_number || "—"} · <b>Engine:</b> {detail.vehicle_details?.engine_number || "—"} · <b>KM:</b> {detail.vehicle_details?.km_reading || "—"}</div>
            <div><b>E-tag:</b> {detail.etag?.present ? "Yes" : "No"} · <b>Licence disc:</b> {detail.licence_disc?.present ? "Yes" : "No"}</div>
            <div><b>ADAS:</b> {detail.adas?.state || "—"}</div>
            <div><b>Damage markers:</b> pre {detail.pre_damage_markers?.length || 0} · post {detail.post_damage_markers?.length || 0}</div>
            <div><b>Return history:</b> {detail.return_history?.length || 0} time(s)</div>
          </div>
          {detail.status === "submitted" ? (
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setReturnFor(detail); }} data-testid="cq-return-btn"
                      className="flex-1 border border-red-300 text-red-700 rounded-md py-2 text-sm font-display uppercase tracking-widest">
                <XCircle size={14} className="inline mr-1" /> Return
              </button>
              <button onClick={approve} disabled={busy} data-testid="cq-approve-btn"
                      className="flex-1 bg-[#f28a16] text-white rounded-md py-2 text-sm font-display uppercase tracking-widest disabled:opacity-50">
                {busy ? <Loader2 className="inline animate-spin" size={14} /> : <CheckCircle2 size={14} className="inline" />} Approve
              </button>
            </div>
          ) : (
            <div className="pt-2">
              <Link to={`/admin/jobs/${detail.job_id}`} className="text-xs text-[#f28a16] underline">Open full job →</Link>
            </div>
          )}
        </Modal>
      )}

      {returnFor && (
        <Modal onClose={() => setReturnFor(null)} testId="cq-return-modal">
          <div className="font-display uppercase tracking-widest text-sm">Return job card #{returnFor.job_number}</div>
          <textarea rows={3} value={note} minLength={3} maxLength={1000} onChange={(e) => setNote(e.target.value)}
                    placeholder="What needs to be corrected? (min 3 chars)"
                    data-testid="cq-return-note" className="w-full border rounded p-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={() => setReturnFor(null)} className="flex-1 border rounded-md py-2 text-sm">Cancel</button>
            <button onClick={doReturn} disabled={busy || note.trim().length < 3} data-testid="cq-return-confirm"
                    className="flex-1 bg-red-600 text-white rounded-md py-2 text-sm font-display uppercase tracking-widest disabled:opacity-50">
              Return
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-5 w-full max-w-md space-y-3" data-testid={testId}>
      {children}
    </div>
  </div>
);
